import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Loader2, CheckCircle, Circle, Clock,
  User, Users, MapPin, Calendar, Target, Plus, Trash2, Save, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => getAccessToken();
const headers = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const FASE_WEKEN = [[1, 2], [3, 4], [5, 6], [7, 8], [9, 12]];
const STATUS_COLORS = {
  actief: 'bg-emerald-100 text-emerald-800', afgerond: 'bg-slate-100 text-slate-600', no_go: 'bg-red-100 text-red-800',
  gepland: 'bg-slate-100 text-slate-500', open: 'bg-amber-100 text-amber-700', behaald: 'bg-emerald-100 text-emerald-700', niet_behaald: 'bg-red-100 text-red-700',
};

function getCurrentWeek(s) {
  if (!s) return 0;
  return Math.max(0, Math.floor((new Date() - new Date(s)) / (7 * 86400000)) + 1);
}

const TABS = [
  { id: 'overzicht', label: 'Overzicht' },
  { id: 'fasen', label: 'Fasen' },
  { id: 'kpis', label: 'KPIs' },
  { id: 'targets', label: 'Targets' },
  { id: 'team', label: 'Team' },
  { id: 'bonussen', label: 'Bonussen' },
  { id: 'notities', label: 'Notities' },
];

export default function ImplementatieDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overzicht');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/implementaties/${slug}`, { headers: headers() });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      toast.error('Implementatie niet gevonden');
      navigate('/management/implementaties');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const patch = async (path, body) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/implementaties/${slug}${path}`, { method: 'PATCH', headers: headers(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      await fetchData();
      toast.success('Opgeslagen');
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const put = async (body) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/implementaties/${slug}`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setData(await res.json());
      toast.success('Opgeslagen');
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-tw-blue" /></div>;
  if (!data) return null;

  const week = getCurrentWeek(data.start_datum);
  const faseIdx = (() => { for (let i = 0; i < FASE_WEKEN.length; i++) { if (week >= FASE_WEKEN[i][0] && week <= FASE_WEKEN[i][1]) return i; } return week <= 0 ? -1 : FASE_WEKEN.length - 1; })();

  return (
    <div className="space-y-6" data-testid="impl-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/management/implementaties')} data-testid="impl-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />Terug
        </Button>
      </div>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{data.locatie_naam}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={STATUS_COLORS[data.status]}>{data.status}</Badge>
            <span className="text-sm text-slate-500">{data.type} | Go/No-Go: {data.go_no_go_datum || '—'}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-slate-700">Week {Math.min(week, 12)} van 12</p>
          <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-tw-blue rounded-full" style={{ width: `${Math.min((week / 12) * 100, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="impl-tabs">
        {TABS.map(t => (
          <Button key={t.id} variant={tab === t.id ? 'default' : 'ghost'} size="sm"
            onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={tab === t.id ? 'bg-tw-blue text-white hover:bg-tw-blue-light shadow-sm' : 'text-slate-600 hover:text-slate-900'}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overzicht' && <TabOverzicht data={data} week={week} faseIdx={faseIdx} />}
      {tab === 'fasen' && <TabFasen data={data} week={week} faseIdx={faseIdx} onPatch={patch} />}
      {tab === 'kpis' && <TabKPIs data={data} onPatch={patch} slug={slug} />}
      {tab === 'targets' && <TabTargets data={data} slug={slug} onRefresh={fetchData} onPatch={patch} />}
      {tab === 'team' && <TabTeam data={data} onSave={put} />}
      {tab === 'bonussen' && <TabBonussen data={data} onPatch={patch} />}
      {tab === 'notities' && <TabNotities data={data} onSave={put} />}
    </div>
  );
}

// ── Tab: Overzicht ──────────────────────────────────────────────────────────

function TabOverzicht({ data, week, faseIdx }) {
  return (
    <div className="space-y-4" data-testid="tab-overzicht">
      {/* Fase timeline */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Fasen</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-2">
            {data.fases?.map((f, i) => (
              <div key={i} className={`flex-1 rounded-lg p-3 border-2 transition-colors ${i === faseIdx ? 'border-tw-blue bg-tw-blue/5' : i < faseIdx ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-slate-50'}`}>
                <p className={`text-xs font-bold ${i === faseIdx ? 'text-tw-blue' : i < faseIdx ? 'text-emerald-600' : 'text-slate-400'}`}>Wk {f.weken}</p>
                <p className="text-sm font-semibold text-slate-700 mt-0.5">{f.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI summary */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">KPIs</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {data.kpis?.map(kpi => {
              const achieved = kpi.naam === 'PK%' ? (kpi.actueel != null && kpi.actueel <= kpi.target) : (kpi.actueel != null && kpi.actueel >= kpi.target);
              return (
                <div key={kpi.naam} className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{kpi.naam}</p>
                  <p className={`text-xl font-bold ${kpi.actueel == null ? 'text-slate-300' : achieved ? 'text-emerald-600' : 'text-red-600'}`}>
                    {kpi.actueel != null ? `${kpi.eenheid === '\u20AC' ? '\u20AC ' : ''}${kpi.actueel}${kpi.eenheid === '%' ? '%' : ''}` : '—'}
                  </p>
                  <p className="text-xs text-slate-400">Target: {kpi.eenheid === '\u20AC' ? '\u20AC ' : ''}{kpi.target}{kpi.eenheid === '%' ? '%' : ''}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Team summary */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Team</CardTitle></CardHeader>
        <CardContent className="pt-0 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">OP Manager:</span> <strong>{data.op_manager || '—'}</strong></div>
          <div><span className="text-slate-500">Interim BL:</span> <strong>{data.interim_bl || '—'}</strong></div>
          <div><span className="text-slate-500">Thuislocatie:</span> <strong>{data.thuislocatie || '—'}</strong></div>
          <div><span className="text-slate-500">TL Thuislocatie:</span> <strong>{data.tl_thuislocatie || '—'}</strong></div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Fasen ──────────────────────────────────────────────────────────────

function TabFasen({ data, week, faseIdx, onPatch }) {
  const [goNoGo, setGoNoGo] = useState(data.go_no_go_besluit);
  const [expanded, setExpanded] = useState(faseIdx >= 0 ? faseIdx : 0);

  const submitGoNoGo = async (besluit) => {
    setGoNoGo(besluit);
    try {
      const res = await fetch(`${API}/implementaties/${data.locatie_slug}/go-no-go`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ besluit }) });
      if (!res.ok) throw new Error();
      toast.success(`Go/No-Go: ${besluit}`);
    } catch { toast.error('Opslaan mislukt'); }
  };

  const toggleActiepunt = async (faseIndex, apIndex, klaar) => {
    try {
      await fetch(`${API}/implementaties/${data.locatie_slug}/fase/${faseIndex}/actiepunt/${apIndex}`, {
        method: 'PATCH', headers: headers(), body: JSON.stringify({ klaar }),
      });
    } catch { toast.error('Opslaan mislukt'); }
  };

  return (
    <div className="space-y-3" data-testid="tab-fasen">
      {data.fases?.map((f, i) => {
        const isCurrent = i === faseIdx;
        const isPast = i < faseIdx || f.status === 'afgerond';
        const isOpen = expanded === i;
        const aps = f.actiepunten || [];
        const apDone = aps.filter(a => a.klaar).length;
        return (
          <div key={i} className={`rounded-lg border-2 overflow-hidden ${isCurrent ? 'border-tw-blue' : isPast ? 'border-emerald-200' : 'border-slate-100'}`}>
            <div
              className={`flex items-center gap-4 p-4 cursor-pointer ${isCurrent ? 'bg-tw-blue/5' : isPast ? 'bg-emerald-50' : ''}`}
              onClick={() => setExpanded(isOpen ? -1 : i)}
              data-testid={`fase-header-${i}`}
            >
              <div className="flex-shrink-0">
                {isPast ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : isCurrent ? <Clock className="w-5 h-5 text-tw-blue" /> : <Circle className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800">{f.label} <span className="text-xs text-slate-400 ml-1">Week {f.weken}</span></p>
                <p className="text-sm text-slate-500">{f.beschrijving}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {aps.length > 0 && <span className="text-xs text-slate-400">{apDone}/{aps.length}</span>}
                <select value={f.status} onChange={e => { e.stopPropagation(); onPatch(`/fase/${i}`, { status: e.target.value }); }}
                  onClick={e => e.stopPropagation()}
                  className="text-xs border rounded px-2 py-1 bg-white" data-testid={`fase-status-${i}`}>
                  <option value="gepland">Gepland</option>
                  <option value="actief">Actief</option>
                  <option value="afgerond">Afgerond</option>
                </select>
              </div>
            </div>
            {isOpen && aps.length > 0 && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-1.5" data-testid={`fase-actiepunten-${i}`}>
                {aps.map((ap, j) => (
                  <label key={j} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1">
                    <input type="checkbox" checked={ap.klaar}
                      onChange={e => { ap.klaar = e.target.checked; toggleActiepunt(i, j, e.target.checked); }}
                      className="w-4 h-4 rounded border-slate-300 text-tw-blue focus:ring-tw-blue"
                      data-testid={`actiepunt-${i}-${j}`} />
                    <span className={`text-sm ${ap.klaar ? 'line-through text-slate-400' : 'text-slate-700'}`}>{ap.tekst}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Go/No-Go besluit */}
      <Card className="border-0 shadow-sm border-l-4 border-l-tw-gold">
        <CardHeader className="pb-2"><CardTitle className="text-base">Go/No-Go Besluit (week 7-8)</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-2">
            {['go', 'bijsturen', 'no_go'].map(b => (
              <Button key={b} size="sm" variant={goNoGo === b ? 'default' : 'outline'}
                onClick={() => submitGoNoGo(b)} data-testid={`go-no-go-${b}`}
                className={goNoGo === b ? (b === 'go' ? 'bg-emerald-600 hover:bg-emerald-700' : b === 'no_go' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600') + ' text-white' : ''}>
                {b === 'go' ? 'Go' : b === 'bijsturen' ? 'Bijsturen' : 'No-Go'}
              </Button>
            ))}
          </div>
          {goNoGo && <p className="text-sm text-slate-500 mt-2">Huidig besluit: <strong className="text-slate-700">{goNoGo}</strong></p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: KPIs ───────────────────────────────────────────────────────────────

function TabKPIs({ data, onPatch, slug }) {
  const [editing, setEditing] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const saveKpi = (naam) => {
    const val = editing[naam];
    if (val === undefined) return;
    onPatch(`/kpi/${encodeURIComponent(naam)}`, { actueel: parseFloat(val) || 0 });
    setEditing(p => { const n = { ...p }; delete n[naam]; return n; });
  };

  const refreshKpis = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/implementaties/${slug}/refresh-kpis`, { method: 'POST', headers: headers() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      if (d.success) {
        toast.success('KPIs bijgewerkt vanuit DailyOps');
        window.location.reload();
      } else {
        toast.info(d.message || 'Geen DailyOps data beschikbaar');
      }
    } catch { toast.error('Ververs mislukt'); }
    finally { setRefreshing(false); }
  };

  const hasDailyOps = data.kpis?.some(k => k.bron === 'dailyops');

  return (
    <div className="space-y-3" data-testid="tab-kpis">
      {hasDailyOps && (
        <div className="flex items-center justify-between">
          <div>
            {data.kpis_updated_at && (
              <p className="text-xs text-slate-400">Laatste DailyOps update: {new Date(data.kpis_updated_at).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={refreshKpis} disabled={refreshing} data-testid="refresh-kpis-btn">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Ververs DailyOps
          </Button>
        </div>
      )}
      {data.kpis?.map(kpi => {
        const isManual = kpi.bron === 'handmatig';
        const achieved = kpi.naam === 'PK%' ? (kpi.actueel != null && kpi.actueel <= kpi.target) : (kpi.actueel != null && kpi.actueel >= kpi.target);
        return (
          <Card key={kpi.naam} className="border-0 shadow-sm" data-testid={`kpi-${kpi.naam}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">{kpi.naam}</p>
                  <p className="text-xs text-slate-400">Target: {kpi.eenheid === '\u20AC' ? '\u20AC ' : ''}{kpi.target}{kpi.eenheid === '%' ? '%' : ''} | Bron: {kpi.bron}</p>
                  {kpi.bron === 'dailyops' && (
                    <p className="text-xs text-blue-500 mt-0.5">DailyOps</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {isManual ? (
                    <div className="flex items-center gap-1.5">
                      <Input type="number" className="w-24 h-8 text-sm" placeholder="Actueel"
                        value={editing[kpi.naam] ?? (kpi.actueel ?? '')}
                        onChange={e => setEditing(p => ({ ...p, [kpi.naam]: e.target.value }))}
                        data-testid={`kpi-input-${kpi.naam}`} />
                      {editing[kpi.naam] !== undefined && (
                        <Button size="sm" variant="ghost" onClick={() => saveKpi(kpi.naam)} className="h-8 px-2"><Save className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  ) : (
                    <span className={`text-lg font-bold ${kpi.actueel == null ? 'text-slate-300' : achieved ? 'text-emerald-600' : 'text-red-600'}`}>
                      {kpi.actueel != null ? `${kpi.eenheid === '\u20AC' ? '\u20AC ' : ''}${kpi.actueel}${kpi.eenheid === '%' ? '%' : ''}` : '\u2014'}
                    </span>
                  )}
                  <Badge className={kpi.actueel == null ? STATUS_COLORS.open : achieved ? STATUS_COLORS.behaald : STATUS_COLORS.niet_behaald}>
                    {kpi.actueel == null ? 'open' : achieved ? 'behaald' : 'niet behaald'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ── Tab: Targets ────────────────────────────────────────────────────────────

function TabTargets({ data, slug, onRefresh, onPatch }) {
  const [newTarget, setNewTarget] = useState({ label: '', waarde: '', deadline: '' });

  const addTarget = async () => {
    if (!newTarget.label) return;
    try {
      await fetch(`${API}/implementaties/${slug}/target`, { method: 'POST', headers: headers(), body: JSON.stringify(newTarget) });
      setNewTarget({ label: '', waarde: '', deadline: '' });
      onRefresh();
      toast.success('Target toegevoegd');
    } catch { toast.error('Toevoegen mislukt'); }
  };

  const deleteTarget = async (idx) => {
    try {
      await fetch(`${API}/implementaties/${slug}/target/${idx}`, { method: 'DELETE', headers: headers() });
      onRefresh();
      toast.success('Target verwijderd');
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const cycleStatus = (idx, current) => {
    const next = current === 'open' ? 'behaald' : current === 'behaald' ? 'niet_behaald' : 'open';
    onPatch(`/target/${idx}`, { status: next });
  };

  return (
    <div className="space-y-3" data-testid="tab-targets">
      {data.targets?.map((t, i) => (
        <Card key={i} className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-800">{t.label}</p>
              <p className="text-xs text-slate-400">{t.waarde} | Deadline: {t.deadline || '—'}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => cycleStatus(i, t.status)} data-testid={`target-status-${i}`}>
                <Badge className={STATUS_COLORS[t.status] || STATUS_COLORS.open}>{t.status}</Badge>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteTarget(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {(!data.targets || data.targets.length === 0) && <p className="text-sm text-slate-400 text-center py-4">Geen targets ingesteld</p>}

      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="p-4">
          <p className="text-sm font-medium text-slate-600 mb-2">Nieuw target</p>
          <div className="flex gap-2">
            <Input placeholder="Label" value={newTarget.label} onChange={e => setNewTarget(p => ({ ...p, label: e.target.value }))} className="flex-1" data-testid="new-target-label" />
            <Input placeholder="Waarde" value={newTarget.waarde} onChange={e => setNewTarget(p => ({ ...p, waarde: e.target.value }))} className="w-28" data-testid="new-target-waarde" />
            <Input placeholder="Deadline" value={newTarget.deadline} onChange={e => setNewTarget(p => ({ ...p, deadline: e.target.value }))} className="w-36" data-testid="new-target-deadline" />
            <Button size="sm" onClick={addTarget} className="bg-tw-blue text-white hover:bg-tw-blue-light" data-testid="add-target-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Team ───────────────────────────────────────────────────────────────

function TabTeam({ data, onSave }) {
  const [form, setForm] = useState({
    op_manager: data.op_manager || '',
    interim_bl: data.interim_bl || '',
    thuislocatie: data.thuislocatie || '',
    tl_thuislocatie: data.tl_thuislocatie || '',
  });

  const Field = ({ label, field }) => (
    <div>
      <label className="text-sm font-medium text-slate-600 mb-1 block">{label}</label>
      <Input value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} data-testid={`team-${field}`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-team">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="OP Manager" field="op_manager" />
          <Field label="Interim BL" field="interim_bl" />
          <Field label="Thuislocatie" field="thuislocatie" />
          <Field label="TL Thuislocatie" field="tl_thuislocatie" />
        </CardContent>
      </Card>
      <Button onClick={() => onSave(form)} className="bg-tw-blue text-white hover:bg-tw-blue-light" data-testid="team-save-btn">
        <Save className="w-4 h-4 mr-1.5" />Opslaan
      </Button>

      <Card className="border-0 shadow-sm bg-amber-50 border-l-4 border-l-amber-400">
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-amber-800">Vent van de tent</p>
          <p className="text-sm text-amber-700 mt-1">Bij de Go/No-Go beoordeling (week 7-8) wordt gekeken of er een geschikte kandidaat is als vaste teamleider. Dit is een van de drie criteria naast KPI-stabiliteit en teamkwaliteit.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Bonussen ────────────────────────────────────────────────────────────

function TabBonussen({ data, onPatch }) {
  const b = data.bonussen || {};
  const bl = b.interim_bl || {};
  const op = b.op_manager || {};

  const toggleBL = (field) => onPatch('/bonus', { [`interim_bl.${field}`]: !bl[field] });
  const toggleOP = (field) => onPatch('/bonus', { [`op_manager.${field}`]: !op[field] });

  const blTotal = (bl.inzetvergoeding_actief ? 200 : 0) + (bl.succes ? 350 : 0) + (bl.retentie ? 350 : 0);

  const BonusRow = ({ label, bedrag, active, onToggle, testId }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div>
        <p className="font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-400">{bedrag}</p>
      </div>
      <Button size="sm" variant={active ? 'default' : 'outline'} onClick={onToggle} data-testid={testId}
        className={active ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}>
        {active ? 'Behaald' : 'Niet behaald'}
      </Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-bonussen">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Interim BL: {data.interim_bl || '—'}</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <BonusRow label="Inzetvergoeding" bedrag={`\u20AC200/mnd`} active={bl.inzetvergoeding_actief} onToggle={() => toggleBL('inzetvergoeding_actief')} testId="bonus-inzet" />
          <BonusRow label="Succesbonus" bedrag={`\u20AC350`} active={bl.succes} onToggle={() => toggleBL('succes')} testId="bonus-succes" />
          <BonusRow label="Retentiebonus" bedrag={`\u20AC350`} active={bl.retentie} onToggle={() => toggleBL('retentie')} testId="bonus-retentie" />
          <div className="pt-3 flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">Totaal</span>
            <span className="text-lg font-bold text-tw-blue">{'\u20AC'}{blTotal} / {'\u20AC'}900 max</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">OP Manager: {data.op_manager || '—'}</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <BonusRow label="Implementatiebonus WKR" bedrag={`\u20AC750`} active={op.implementatie} onToggle={() => toggleOP('implementatie')} testId="bonus-impl" />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Notities ───────────────────────────────────────────────────────────

function TabNotities({ data, onSave }) {
  const [text, setText] = useState(data.notities || '');
  const [dirty, setDirty] = useState(false);

  const save = () => { onSave({ notities: text }); setDirty(false); };

  return (
    <div className="space-y-3" data-testid="tab-notities">
      <Textarea value={text} onChange={e => { setText(e.target.value); setDirty(true); }}
        placeholder="Notities over deze implementatie..." rows={10} className="resize-y" data-testid="notities-textarea" />
      <Button onClick={save} disabled={!dirty} className="bg-tw-blue text-white hover:bg-tw-blue-light" data-testid="notities-save-btn">
        <Save className="w-4 h-4 mr-1.5" />Opslaan
      </Button>
    </div>
  );
}
